import { clsx, type ClassValue } from 'clsx'
import { useState } from 'react'
import { useFormAction, useNavigation } from 'react-router'
import { optimize } from 'svgo/browser'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

export function getDomainUrl(request: Request) {
	const host =
		request.headers.get('X-Forwarded-Host') ??
		request.headers.get('host') ??
		new URL(request.url).host
	const protocol = request.headers.get('X-Forwarded-Proto') ?? 'http'
	return `${protocol}://${host}`
}

/**
 * Combine multiple header objects into one (uses append so headers are not overridden)
 */
export function combineHeaders(
	...headers: Array<ResponseInit['headers'] | null | undefined>
) {
	const combined = new Headers()
	for (const header of headers) {
		if (!header) continue
		for (const [key, value] of new Headers(header).entries()) {
			combined.append(key, value)
		}
	}
	return combined
}

/**
 * Combine multiple response init objects into one (uses combineHeaders)
 */
export function combineResponseInits(
	...responseInits: Array<ResponseInit | null | undefined>
) {
	let combined: ResponseInit = {}
	for (const responseInit of responseInits) {
		combined = {
			...responseInit,
			headers: combineHeaders(combined.headers, responseInit?.headers),
		}
	}
	return combined
}

/**
 * Returns true if the current navigation is submitting the current route's
 * form. Defaults to the current route's form action and method POST.
 *
 * Defaults state to 'non-idle'
 *
 * NOTE: the default formAction will include query params, but the
 * navigation.formAction will not, so don't use the default formAction if you
 * want to know if a form is submitting without specific query params.
 */
export function useIsPending({
	formAction,
	formMethod = 'POST',
	state = 'non-idle',
}: {
	formAction?: string
	formMethod?: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE'
	state?: 'submitting' | 'loading' | 'non-idle'
} = {}) {
	const contextualFormAction = useFormAction()
	const navigation = useNavigation()
	const isPendingState =
		state === 'non-idle'
			? navigation.state !== 'idle'
			: navigation.state === state
	return (
		isPendingState &&
		navigation.formAction === (formAction ?? contextualFormAction) &&
		navigation.formMethod === formMethod
	)
}

function callAll<Args extends Array<unknown>>(
	...fns: Array<((...args: Args) => unknown) | undefined>
) {
	return (...args: Args) => fns.forEach((fn) => fn?.(...args))
}

/**
 * Use this hook with a button and it will make it so the first click sets a
 * `doubleCheck` state to true, and the second click will actually trigger the
 * `onClick` handler. This allows you to have a button that can be like a
 * "are you sure?" experience for the user before doing destructive operations.
 */
export function useDoubleCheck() {
	const [doubleCheck, setDoubleCheck] = useState(false)

	function getButtonProps(
		props?: React.ButtonHTMLAttributes<HTMLButtonElement>,
	) {
		const onBlur: React.ButtonHTMLAttributes<HTMLButtonElement>['onBlur'] =
			() => setDoubleCheck(false)

		const onClick: React.ButtonHTMLAttributes<HTMLButtonElement>['onClick'] =
			doubleCheck
				? undefined
				: (e) => {
						e.preventDefault()
						setDoubleCheck(true)
					}

		const onKeyUp: React.ButtonHTMLAttributes<HTMLButtonElement>['onKeyUp'] = (
			e,
		) => {
			if (e.key === 'Escape') {
				setDoubleCheck(false)
			}
		}

		return {
			...props,
			onBlur: callAll(onBlur, props?.onBlur),
			onClick: callAll(onClick, props?.onClick),
			onKeyUp: callAll(onKeyUp, props?.onKeyUp),
		}
	}

	return { doubleCheck, getButtonProps }
}

export function getCustomizedSVG(
	svg: string,
	color: string = 'currentColor',
	strokeWidth: number = 1.5,
) {
	const svgAttributes = svg.match(/<svg[^>]*>/)?.[0]
	if (!svgAttributes) {
		throw new Error('invalid svg')
	}

	if (svg.includes('<style>')) {
		try {
			const { data } = optimize(svg, {
				multipass: true,
				plugins: [
					{
						name: 'inlineStyles',
						params: {
							onlyMatchedOnce: false,
						},
					},
					{
						name: 'convertStyleToAttrs',
					},
				],
			})
			svg = data
		} catch (e) {
			console.error(e)
		}
	}

	// remove <style></style> and everything inside it
	// svg = svg.replace(/<style[\s\S]*?<\/style>/g, '')

	const width = svgAttributes.match(/width="([^"]*)"/)?.[1]
	const height = svgAttributes.match(/height="([^"]*)"/)?.[1]
	const viewBox = svgAttributes.match(/viewBox="([^"]*)"/)?.[1]?.split(' ')
	const viewBoxWidth = viewBox?.[2]
	const viewBoxHeight = viewBox?.[3]

	let size: number | null = null
	if (width && height) {
		size = parseInt(width)
	} else if (viewBoxWidth && viewBoxHeight) {
		size = parseInt(viewBoxWidth)
	} else {
		// set a default size
		size = 24
	}

	if (size) {
		size = Math.max(Math.min(size, 24), 16) // iconsax has some icons with width 25 and height 24
	}

	if (width && height) {
		svg = svg.replace(/width="([^"]*)"/, `width="100%"`)
		svg = svg.replace(/height="([^"]*)"/, `height="100%"`)
	} else {
		svg = svg.replace(/<svg/, `<svg width="100%" height="100%"`)
	}

	let hasElementsWithValidColors = false
	if (svg.includes('fill="') || svg.includes('stroke="')) {
		svg = svg.replace(/(fill|stroke)="([^"]+)"/g, (match, attr, value) => {
			if (value === 'none' || value === 'transparent') {
				return match
			}
			// For iconsax -> Crypto -> Bulk
			if (value === 'white') {
				return ''
			}

			hasElementsWithValidColors = true
			return `${attr}="${color}"`
		})
	}

	if (!hasElementsWithValidColors) {
		svg = svg.replace(
			/<(path|circle|rect|line|polygon|polyline) /g,
			(match) => `<${match.slice(1)} fill="${color}" `,
		)
	}

	svg = svg.replace(/stroke-width="([^"]+)"/g, (match, value) => {
		if (value === '0') {
			return match
		}
		return `stroke-width="${strokeWidth}"`
	})

	return {
		size,
		svg,
	}
}

export const reconstructIconPath = ({
	path,
	name,
}: {
	path: string | undefined
	name: string
}) => {
	if (path) {
		return `${path}/${name}`
	}
	return name
}

export const DEFAULT_DEBOUNCE_TIME = 250
export const DEFAULT_COLOR = 'hsb(252, 59%, 76%)'
export const DEFAULT_STROKE_WIDTH = 1.5
export const DEFAULT_COLOR_SPACE = 'hex'
export const DEFAULT_DARK_MODE = 'false'

export const ICON_CARD_GRID_COLUMN_GAP = 16
export const ICON_CARD_GRID_ROW_GAP = 20
export const ICON_CARD_MIN_WIDTH = 146
export const ICON_CARD_NAME_HEIGHT = 18
export const ICON_CARD_GAP = 8
export const DIRECTORY_HEADER_HEIGHT = 48

// Shared types for icon data - all data sources must conform to these
export type IconWithId = {
	id: string
	name: string
	path: string
	svg: string
	color: string | null
	strokeWidth: number | null
	originalIconId?: string | null
}

export type DirectoryWithIcons = {
	path: string
	breadcrumbs: string[]
	icons: IconWithId[]
}

export type DirectoriesWithIconsBySearch = DirectoryWithIcons[]

export function formatCount(count: number) {
	return new Intl.NumberFormat('en-US').format(count)
}

export function getSsrHasMoreIcons(
	ssrDirectories?: Array<{ icons: unknown[]; actualIconCount: number }> | null,
) {
	if (!ssrDirectories) return false
	const ssrIconCount = ssrDirectories.reduce(
		(sum, dir) => sum + dir.icons.length,
		0,
	)
	const actualIconCount = ssrDirectories.reduce(
		(sum, dir) => sum + dir.actualIconCount,
		0,
	)
	return ssrIconCount < actualIconCount
}
